import { runCreateFolderWorkflow } from "@/action-dialog";
import { useListRelationships } from "@/api/relations";
import { RailReveal } from "@/components/rail-kit/RailReveal";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { RailWorkspaceExpandableRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailExpandableRow";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { FilesRailDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDropTarget";
import {
  FilesRailTree,
  type FilesRailCanvasActions,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailTree";
import { useFilesRailWorkspaceFiles } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  createWorkspaceActionHandlers,
  useObjectActions,
  type ObjectKind,
} from "@/object-actions";
import { useActiveFileRecordId } from "@/panes/file-record-detail/useActiveFileRecordId";
import { useOpenFileRecord } from "@/panes/file-record-detail/useOpenFileRecord";
import type { Canvas, OrganizationUser, Workspace } from "@cohesive/api-client";
import {
  PersonAvatar,
  Icon as WorkspaceMetadataIcon,
} from "@cohesive/ui-components";
import { Layers, UserPlus, Users, type LucideIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

const WORKSPACE_TEAM_KIND: ObjectKind = { type: "workspace", scope: "team" };
const WORKSPACE_PRIVATE_KIND: ObjectKind = {
  type: "workspace",
  scope: "private",
};

function WorkspaceMembersIndicator({
  workspace,
  members,
  onShare,
}: {
  workspace: Workspace;
  members: OrganizationUser[];
  onShare: () => void;
}) {
  return (
    <HoverCard openDelay={500} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          role="img"
          aria-label={`Shared workspace — ${members.length} ${members.length === 1 ? "member" : "members"}`}
          className="inline-flex size-4 shrink-0 items-center justify-center text-n-50 hover:text-n-80"
          onClick={(e) => e.stopPropagation()}
        >
          <Users className="size-3.5" />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={20}
        className="w-[260px] p-2 rounded-2xl border dark:border-white/[0.08] shadow-xl shadow-black/10 bg-background/95 backdrop-blur-lg"
      >
        <div className="flex items-center justify-between gap-2 pl-3 pr-1.5 pt-1.5 pb-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            Members
          </p>
          <button
            type="button"
            aria-label="Add members"
            onClick={(e) => {
              e.stopPropagation();
              window.setTimeout(onShare, 0);
            }}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] cursor-pointer"
          >
            <UserPlus className="h-4 w-4" />
          </button>
        </div>
        <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
        <ul className="max-h-64 overflow-y-auto -mr-1 pr-1">
          {members.map((m) => (
            <li
              key={m.id}
              className="flex items-center gap-2.5 h-10 rounded-lg px-3"
            >
              <PersonAvatar
                name={m.name}
                email={m.email}
                pictureUrl={m.picture_url}
                className="size-7 text-[11px]"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-foreground leading-tight">
                  {m.name}
                </p>
                {m.id === workspace.user_id && (
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5 leading-tight">
                    Owner
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

export function WorkspacesRailWorkspaceRow({
  workspace,
  active,
  activeCanvasId,
  currentUserId,
  usersById,
  onRename,
  onShare,
  onDelete,
  onRenameCanvas,
  onShareCanvas,
  onDeleteCanvas,
  onNewCanvas,
  onNewChat,
  onOpen,
  newCanvasDisabled,
  labelOverride,
  icon: Icon = Layers,
  trailingSlot,
  hideWorkspaceMenu,
  defaultOpen = false,
}: {
  workspace: Workspace;
  active?: boolean;
  activeCanvasId: string | null;
  currentUserId?: string;
  usersById: Map<string, OrganizationUser>;
  onRename: () => void;
  onShare: () => void;
  onDelete?: () => void;
  onRenameCanvas: (canvas: Canvas) => void;
  onShareCanvas: (canvas: Canvas) => void;
  onDeleteCanvas: (canvas: Canvas) => void;
  /** Create a canvas in this workspace at `parentPath` ("" = root). */
  onNewCanvas?: (parentPath?: string) => void;
  /** Create a new workspace chat. */
  onNewChat?: () => void;
  /** Navigate to the workspace detail page (title double-click; also in the
   *  kebab/right-click menu — a single title click toggles expansion). */
  onOpen?: () => void;
  newCanvasDisabled?: boolean;
  labelOverride?: string;
  icon?: LucideIcon;
  trailingSlot?: React.ReactNode;
  hideWorkspaceMenu?: boolean;
  /** Expand the row (revealing its sections) on first render. */
  defaultOpen?: boolean;
}) {
  const menu = useRailRowContextMenu();
  const [open, setOpen] = useRailExpansion(`ws:${workspace.id}`, defaultOpen);
  const activeFileRecordId = useActiveFileRecordId();
  const openFileRecord = useOpenFileRecord();

  // One tree per workspace: folders, the canvases filed in them, and files.
  // Fetched only once the workspace row is actually open — the persisted query
  // cache supplies the warmth from the second session on.
  const {
    fileRecords,
    uploadInputs,
    dialogs,
    actions,
    menuActions,
    uploadQueue,
    isPending: treeLoading,
    handleUploadClick,
    handleUploadToPath,
    handleUploadFolderClick,
    handleUploadFolderToPath,
    getOsDropHandler,
  } = useFilesRailWorkspaceFiles(workspace.id, { enabled: open });

  const openNewFolder = useCallback(
    (currentPath: string) => {
      window.setTimeout(
        () =>
          void runCreateFolderWorkflow({
            workspaceId: workspace.id,
            currentPath,
          }),
        0,
      );
    },
    [workspace.id],
  );

  const handleNewDocumentAtPath = useCallback(
    (parentPath: string) => {
      void uploadQueue.createDoc(parentPath, {
        onCreated: (record) => openFileRecord(record.id),
      });
    },
    [uploadQueue, openFileRecord],
  );

  // The canvases in this workspace, read off the tree — the rows carry their
  // canvas, so no second request.
  const treeEmpty = !fileRecords?.some(
    (record) => record.status !== "pending" && record.status !== "deleted",
  );

  // Every workspace row gets a kebab (+ matching right-click menu) that leads
  // with the create actions; folder-scoped creation lives on the folder rows.
  // Team workspaces add Edit / Add Members / Archive; "My Workspace" can't be
  // edited, shared or archived, so its menu is create-only.
  const workspaceKind = hideWorkspaceMenu
    ? WORKSPACE_PRIVATE_KIND
    : WORKSPACE_TEAM_KIND;

  const workspaceActionHandlers = useMemo(
    () =>
      createWorkspaceActionHandlers(workspace, {
        newCanvas:
          onNewCanvas && !newCanvasDisabled ? () => onNewCanvas() : undefined,
        newChat: onNewChat ? () => onNewChat() : undefined,
        openWorkspace: onOpen ? () => onOpen() : undefined,
        newDocument: () =>
          window.setTimeout(() => handleNewDocumentAtPath(""), 0),
        newFolder: () => openNewFolder(""),
        uploadFiles: () => window.setTimeout(handleUploadClick, 0),
        uploadFolder: () => window.setTimeout(handleUploadFolderClick, 0),
        ...(hideWorkspaceMenu
          ? {}
          : {
              editWorkspace: () => onRename(),
              shareWorkspace: () => onShare(),
              deleteWorkspace: onDelete ? () => onDelete() : undefined,
            }),
      }),
    [
      workspace,
      hideWorkspaceMenu,
      onNewCanvas,
      onNewChat,
      onOpen,
      newCanvasDisabled,
      onRename,
      onShare,
      onDelete,
    ],
  );

  const { actions: workspaceActions } = useObjectActions({
    kind: workspaceKind,
    surface: "left-rail",
    enabled: menu.open,
    // My Workspace is always the user's own; team workspaces answer from the
    // server-computed capabilities on the workspace response.
    state: {
      canEdit: hideWorkspaceMenu || workspace.can_edit !== false,
      canShare: hideWorkspaceMenu || workspace.can_manage_members !== false,
    },
    handlers: workspaceActionHandlers,
  });

  const { data: relationships } = useListRelationships(
    "workspace",
    workspace.id,
  );

  const members = useMemo<OrganizationUser[]>(() => {
    if (!relationships) return [];
    const memberIds = new Set<string>();
    memberIds.add(workspace.user_id);
    for (const rel of relationships) {
      if (rel.subject_object_type === "organization_user") {
        memberIds.add(rel.subject_object_id);
      }
    }
    return Array.from(memberIds)
      .map((id) => usersById.get(id))
      .filter((u): u is OrganizationUser => !!u);
  }, [relationships, usersById, workspace.user_id]);

  const isSharedWithMe = !!currentUserId && workspace.user_id !== currentUserId;
  const showMembersIndicator = isSharedWithMe || members.length > 1;

  // Share and Archive appear on a canvas row only where the user holds the
  // right; capabilities ride on the canvas responses themselves.
  const canvasActions = useCallback<FilesRailCanvasActions>(
    (canvas) => ({
      onRename: onRenameCanvas,
      onShare:
        canvas.can_manage_assignments !== false ? onShareCanvas : undefined,
      onDelete: canvas.can_delete !== false ? onDeleteCanvas : undefined,
    }),
    [onRenameCanvas, onShareCanvas, onDeleteCanvas],
  );

  const handleNewCanvasAtPath = useCallback(
    (parentPath: string) => onNewCanvas?.(parentPath),
    [onNewCanvas],
  );

  const workspaceMenuNode = (
    <RailRowMenu
      {...railRowMenuProps(menu)}
      label={labelOverride ?? workspace.name}
      contentWidthClassName="w-52"
    >
      <ObjectActionMenu
        actions={workspaceActions}
        kind={workspaceKind}
        name={workspace.name}
      />
    </RailRowMenu>
  );

  return (
    // The whole workspace row is its own drop zone, header included: with
    // canvases and files in one tree there is no section to aim at any more,
    // so dropping anywhere on a workspace files the item at its root. Rows
    // inside (folders, canvases) are smaller zones and win under the cursor.
    // Wrapping the row rather than its contents also keeps the zone alive
    // while the workspace is collapsed.
    <FilesRailDropTarget
      workspaceId={workspace.id}
      parentPath=""
      onOsDrop={getOsDropHandler("")}
      data-testid="rail-workspace-tree"
    >
      {/* Outside RailReveal on purpose: it unmounts its children when closed,
          and the workspace kebab offers the uploads whether or not the row
          is open. */}
      {uploadInputs}
      <RailWorkspaceExpandableRow
        icon={Icon}
        // Prefer the workspace's own chosen (colored) icon; fall back to the
        // generic Lucide icon when none is set.
        iconNode={
          workspace.metadata?.icon ? (
            <WorkspaceMetadataIcon icon={workspace.metadata.icon} size={16} />
          ) : undefined
        }
        label={labelOverride ?? workspace.name}
        active={active}
        open={open}
        onOpenChange={setOpen}
        onOpen={onOpen}
        trailing={
          showMembersIndicator || trailingSlot ? (
            <span className="inline-flex items-center gap-1">
              {showMembersIndicator ? (
                <WorkspaceMembersIndicator
                  workspace={workspace}
                  members={members}
                  onShare={onShare}
                />
              ) : null}
              {trailingSlot}
            </span>
          ) : undefined
        }
        menu={workspaceMenuNode}
        onContextMenu={menu.onContextMenu}
      >
        {/* An expanded workspace opens straight into its tree: folders first,
          then the canvases and files inside them. RailReveal keeps the subtree
          mounted only while open (plus the collapse animation) so the queries
          and dialogs stay lazy. The whole tree is the workspace's organizing
          surface — dropping a canvas or a file on it files the item at the
          workspace root; folder rows inside win on hover. */}
        <RailReveal open={open} contentClassName="flex flex-col">
          <div className="flex flex-col">
            {treeLoading ? null : treeEmpty ? (
              <RailZeroState
                icon={Layers}
                accent="teal"
                variant="passive"
                title="Nothing here yet."
                description="Canvases and files in this workspace live here."
              />
            ) : (
              <FilesRailTree
                records={fileRecords ?? []}
                workspaceId={workspace.id}
                activeFileRecordId={activeFileRecordId}
                activeCanvasId={activeCanvasId}
                canvasActions={canvasActions}
                onOpenFile={actions.openFile}
                menuActions={menuActions}
                getOsDropHandler={getOsDropHandler}
                onUploadToPath={handleUploadToPath}
                onUploadFolderToPath={handleUploadFolderToPath}
                onCreateDocumentAtPath={handleNewDocumentAtPath}
                onCreateSubfolder={openNewFolder}
                onCreateCanvasAtPath={handleNewCanvasAtPath}
              />
            )}
            {dialogs}
          </div>
        </RailReveal>
      </RailWorkspaceExpandableRow>
    </FilesRailDropTarget>
  );
}
